// Implements: F-RPT-005, F-UIX-018
/**
 * Mockup v3 P9 Consumo "Costo por modelo y flujo" (v3 `:1812`): the organization's cost per model
 * or per flow version, one toggle between the two ("Por modelo" / "Por flujo", `aria-pressed`)
 * instead of two long tables. Pure presentation; money is formatted here (OD-12).
 *
 * Money is never added across currencies: one table per currency, each row's "Parte del gasto"
 * the share of its own currency's total (`byCurrency`), with the currency named over each table
 * when there are several. An empty report is the empty state, never a 0 % bar.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import { moneyText } from "../../../shared/ui/moneyText";
import type { CostReportView, FlowVersionCostView, ModelCostView } from "../../../types/reports";
import { byCurrency } from "../model/costShares";
import type { CurrencyGroup } from "../model/costShares";
import styles from "./CostBreakdown.module.css";
import tableStyles from "./MyUsage.module.css";
import shareStyles from "./ScopeConsumption.module.css";

export type CostGrouping = "model" | "flow";

export interface CostBreakdownProps {
  readonly report: CostReportView;
  readonly by: CostGrouping;
  readonly onBy: (by: CostGrouping) => void;
}

type Translate = ReturnType<typeof useTranslation>["t"];

function Share({ share, t }: { readonly share: number; readonly t: Translate }): ReactElement {
  return (
    <div className={shareStyles.share}>
      <progress className={shareStyles.bar} value={share} max={100} aria-label={t("reports.costs.column.share")} />
      <span className={shareStyles.percent}>{t("reports.costs.share", { percent: share })}</span>
    </div>
  );
}

function Caption({ currency, several, t }: { readonly currency: string; readonly several: boolean; readonly t: Translate }) {
  return several ? <caption className={styles.caption}>{t("reports.costs.currency", { currency })}</caption> : null;
}

function ModelTable({ group, several, t }: { readonly group: CurrencyGroup<ModelCostView>; readonly several: boolean; readonly t: Translate }) {
  return (
    <table className={tableStyles.table}>
      <Caption currency={group.currency} several={several} t={t} />
      <thead>
        <tr>
          <th scope="col">{t("reports.costs.column.model")}</th>
          <th scope="col">{t("reports.costs.column.calls")}</th>
          <th scope="col">{t("reports.costs.column.cost")}</th>
          <th scope="col">{t("reports.costs.column.share")}</th>
        </tr>
      </thead>
      <tbody>
        {group.rows.map(({ row, share }) => (
          <tr key={row.model}>
            <th scope="row">{row.model}</th>
            <td className={styles.number}>{row.calls}</td>
            <td className={styles.number}>{moneyText(row.cost_micros, row.currency)}</td>
            <td>
              <Share share={share} t={t} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FlowTable({ group, several, t }: { readonly group: CurrencyGroup<FlowVersionCostView>; readonly several: boolean; readonly t: Translate }) {
  return (
    <table className={tableStyles.table}>
      <Caption currency={group.currency} several={several} t={t} />
      <thead>
        <tr>
          <th scope="col">{t("reports.costs.column.flow")}</th>
          <th scope="col">{t("reports.costs.column.generations")}</th>
          <th scope="col">{t("reports.costs.column.delivered")}</th>
          <th scope="col">{t("reports.costs.column.failed")}</th>
          <th scope="col">{t("reports.costs.column.cancelled")}</th>
          <th scope="col">{t("reports.costs.column.cost")}</th>
          <th scope="col">{t("reports.costs.column.share")}</th>
        </tr>
      </thead>
      <tbody>
        {group.rows.map(({ row, share }) => (
          <tr key={row.flow_version}>
            <th scope="row" className={styles.mono}>
              {row.flow_version}
            </th>
            <td className={styles.number}>{row.generations}</td>
            <td className={styles.number}>{row.delivered}</td>
            <td className={styles.number}>{row.failed}</td>
            <td className={styles.number}>{row.cancelled}</td>
            <td className={styles.number}>{moneyText(row.cost_micros, row.currency)}</td>
            <td>
              <Share share={share} t={t} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CostBreakdown({ report, by, onBy }: CostBreakdownProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const models = byCurrency(report.by_model);
  const flows = byCurrency(report.by_flow_version);
  const groups = by === "model" ? models : flows;
  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <div className={styles.head}>
        <h3 id={headingId} className={styles.title}>
          {t("reports.costs.title")}
        </h3>
        <SegmentedControl
          label={t("reports.costs.by.label")}
          options={[
            { value: "model", label: t("reports.costs.by.model") },
            { value: "flow", label: t("reports.costs.by.flow") },
          ]}
          value={by}
          onChange={onBy}
        />
      </div>
      {groups.length === 0 ? (
        <EmptyState icon="chart" headingLevel={4} title={t("reports.costs.empty.title")} body={t("reports.costs.empty.body")} />
      ) : (
        <div className={styles.wrap}>
          {by === "model"
            ? models.map((group) => <ModelTable key={group.currency} group={group} several={models.length > 1} t={t} />)
            : flows.map((group) => <FlowTable key={group.currency} group={group} several={flows.length > 1} t={t} />)}
        </div>
      )}
    </section>
  );
}
